import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import { collection, deleteDoc, doc, getDocs, limit as fsLimit, orderBy, query, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { apiTry } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import AgencyCalendarCard from '../components/AgencyCalendarCard';

// Solicitudes «¿Tu agencia no aparece? Aplica aquí» del Explorador de
// Agencias de la app. Las crea el Worker (POST /api/agency/apply) en
// agency_applications; aquí se revisan y se marca su estado. Arriba, la
// conexión con Google Calendar de las reuniones (AgencyCalendarCard).
// La reunión ya no se reserva en el formulario: a la agencia le llega un
// correo con el enlace a la página de reserva (el del meetingToken de su
// solicitud), que aquí se puede copiar para reenviárselo.

const API_BASE = (import.meta.env.VITE_API_BASE || 'https://ws.tikcontrol.live').replace(/\/+$/, '');
const bookingUrl = (it) => `${API_BASE}/api/agency/apply/meeting?id=${encodeURIComponent(it.id)}&k=${encodeURIComponent(it.meetingToken)}`;

const FETCH_LIMIT = 500;

const APPLICATION_STATUS = [
  { key: 'new', label: 'Nueva', color: 'primary' },
  { key: 'contacted', label: 'Contactada', color: 'success' },
  { key: 'discarded', label: 'Descartada', color: 'default' }
];
const STATUS_BY_KEY = Object.fromEntries(APPLICATION_STATUS.map((s) => [s.key, s]));

function formatTs(ts) {
  const d = ts?.toDate?.();
  return d ? d.toLocaleString() : '—';
}

// Reunión que la agencia reservó en el formulario (las horas son de Madrid).
const MEETING_OPTS = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
const MEETING_FMT = new Intl.DateTimeFormat('es-ES', { ...MEETING_OPTS, timeZone: 'Europe/Madrid' });
function formatMeeting(ts, pending) {
  const d = ts?.toDate?.();
  return d ? MEETING_FMT.format(d) : (pending ? 'Sin reservar' : 'Sin reunión');
}
// La misma reunión en la zona horaria en la que la agencia la reservó
// (meetingTz); '' si no la hay o si allí es la misma hora que en Madrid.
function formatMeetingLocal(ts, tz) {
  const d = ts?.toDate?.();
  if (!d || !tz) return '';
  try {
    const local = new Intl.DateTimeFormat('es-ES', { ...MEETING_OPTS, timeZone: tz }).format(d);
    return local === MEETING_FMT.format(d) ? '' : `${local} · ${tz.replace(/_/g, ' ')}`;
  } catch {
    return '';
  }
}

// Reunión por celebrar que no está en el Google Calendar del equipo: se
// reservó con Google sin conectar, caducado o caído (la reserva no lo espera).
function missingInCalendar(it) {
  const d = it.meetingAt?.toDate?.();
  return !!d && d.getTime() > Date.now() && !it.calendarEventId && it.status !== 'discarded';
}
const CALENDAR_ERRORS = {
  not_configured: 'Google Calendar no está activado en el servidor.',
  not_connected: 'No hay ninguna cuenta de Google conectada: inicia sesión con Google arriba y vuelve a intentarlo.',
  expired: 'La conexión con Google ha caducado: inicia sesión otra vez con Google arriba y vuelve a intentarlo.',
  google_error: 'Google no ha podido crear la reunión. Inténtalo de nuevo en unos minutos.',
  meeting_passed: 'Esa reunión ya ha pasado.',
  no_meeting: 'Esa solicitud no tiene reunión.'
};

// "LATAM / México" → "México · LATAM"
function regionLabel(region) {
  const [group, place] = String(region || '').split(' / ');
  return place ? `${place} · ${group}` : group || '—';
}

export default function AgenciaSolicitudes() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const snap = await getDocs(query(collection(db, 'agency_applications'), orderBy('createdAt', 'desc'), fsLimit(FETCH_LIMIT)));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('[Solicitudes] load:', e);
      setError(e.message || 'Error cargando solicitudes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const acc = { all: items.length, new: 0, contacted: 0, discarded: 0 };
    for (const it of items) if (acc[it.status] !== undefined) acc[it.status]++;
    return acc;
  }, [items]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((it) =>
      (statusFilter === 'all' || it.status === statusFilter) &&
      (!needle || [it.name, it.region, it.tiktok, it.email, it.whatsapp, it.website].some((v) => String(v || '').toLowerCase().includes(needle))));
  }, [items, statusFilter, search]);

  // Descartada o borrada: su reunión sale del Google Calendar y la hora queda
  // libre para otra agencia. Devuelve false si Google no la pudo borrar.
  async function releaseMeeting(it) {
    if (!it.calendarEventId) return true;
    const res = await apiTry('/api/backoffice/agency-calendar/release', { method: 'POST', body: { applicationId: it.id } });
    if (res.ok) setItems((list) => list.map((x) => (x.id === it.id ? { ...x, calendarEventId: null, meetUrl: null } : x)));
    return res.ok;
  }

  // «Crear en Google Calendar»: la reunión que se quedó sin evento. Si sale
  // con Google Meet, el Worker le manda a la agencia el correo con el enlace.
  // «Creando…» va en la propia fila (creating): la tabla solo repinta una fila
  // cuando cambia su elemento, no por un estado de fuera.
  async function addToCalendar(it) {
    if (it.creating) return;
    const patch = (extra) => setItems((list) => list.map((x) => (x.id === it.id ? { ...x, ...extra } : x)));
    patch({ creating: true });
    setError(null);
    setNotice(null);
    const res = await apiTry('/api/backoffice/agency-calendar/book', { method: 'POST', body: { applicationId: it.id } });
    if (!res.ok || !res.data?.calendarEventId) {
      patch({ creating: false });
      setError(`«${it.name}»: ${CALENDAR_ERRORS[res.data?.error] || 'no se pudo crear la reunión en Google Calendar.'}`);
      return;
    }
    patch({ creating: false, calendarEventId: res.data.calendarEventId, meetUrl: res.data.meetUrl || null });
    setNotice(`La reunión de «${it.name}» ya está en Google Calendar.${res.data.emailed ? ' Le hemos enviado a la agencia el correo con el enlace de la videollamada.' : ''}`);
  }

  async function setStatus(it, status) {
    if (!status || status === it.status) return;
    const prev = it.status;
    setItems((list) => list.map((x) => (x.id === it.id ? { ...x, status } : x)));
    try {
      await updateDoc(doc(db, 'agency_applications', it.id), { status, statusAt: new Date() });
    } catch (e) {
      setItems((list) => list.map((x) => (x.id === it.id ? { ...x, status: prev } : x)));
      setError(e.message || 'No se pudo cambiar el estado');
      return;
    }
    if (status === 'discarded' && !(await releaseMeeting(it))) {
      setError(`No se pudo borrar del Google Calendar la reunión de «${it.name}»: bórrala a mano para liberar la hora.`);
    }
  }

  async function remove(it) {
    if (!window.confirm(`¿Borrar la solicitud de «${it.name}»?`)) return;
    const released = await releaseMeeting(it);
    try {
      await deleteDoc(doc(db, 'agency_applications', it.id));
      setItems((list) => list.filter((x) => x.id !== it.id));
      if (!released) setError(`No se pudo borrar del Google Calendar la reunión de «${it.name}»: bórrala a mano para liberar la hora.`);
    } catch (e) {
      setError(e.message || 'No se pudo borrar');
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">Solicitudes de agencias</h1>
          <p className="text-sm text-default-500">Agencias que han pedido aparecer en el Explorador desde «¿Tu agencia no aparece? Aplica aquí».</p>
        </div>
        <Button variant="flat" onPress={load} isDisabled={loading}>Recargar</Button>
      </div>

      <AgencyCalendarCard canWrite={canWrite} />

      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Buscar por nombre, región, TikTok…" value={search} onValueChange={setSearch} isClearable size="sm" className="max-w-sm" />
        <Tabs size="sm" selectedKey={statusFilter} onSelectionChange={(k) => setStatusFilter(String(k))} aria-label="Filtrar por estado">
          {APPLICATION_STATUS.map((s) => <Tab key={s.key} title={`${s.label}s (${counts[s.key]})`} />)}
          <Tab key="all" title={`Todas (${counts.all})`} />
        </Tabs>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>}
      {notice && <div className="rounded-md border border-success-200 bg-success-50 p-3 text-sm text-success-700">{notice}</div>}

      <Table aria-label="Solicitudes de agencias" removeWrapper>
        <TableHeader>
          <TableColumn>AGENCIA</TableColumn>
          <TableColumn>REGIÓN</TableColumn>
          <TableColumn>CONTACTO</TableColumn>
          <TableColumn>REUNIÓN (MADRID)</TableColumn>
          <TableColumn>ENVIADA</TableColumn>
          <TableColumn>ESTADO</TableColumn>
          <TableColumn> </TableColumn>
        </TableHeader>
        <TableBody
          items={filtered}
          isLoading={loading}
          loadingContent={<Spinner />}
          emptyContent={loading ? ' ' : 'No hay solicitudes'}
        >
          {(it) => (
            <TableRow key={it.id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{it.name}</span>
                  {it.website && <a className="text-xs text-primary hover:underline" href={it.website} target="_blank" rel="noopener noreferrer">{it.website.replace(/^https?:\/\//, '')}</a>}
                </div>
              </TableCell>
              <TableCell>{regionLabel(it.region)}</TableCell>
              <TableCell>
                <div className="flex flex-col text-sm">
                  <a className="text-primary hover:underline" href={`https://www.tiktok.com/@${encodeURIComponent(it.tiktok || '')}`} target="_blank" rel="noopener noreferrer">@{it.tiktok}</a>
                  {it.email && <a className="hover:underline" href={`mailto:${it.email}`}>{it.email}</a>}
                  {it.whatsapp && <a className="hover:underline" href={`https://wa.me/${it.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">WhatsApp {it.whatsapp}</a>}
                  {it.source === 'web' ? (
                    <span className="text-xs text-default-500" title="Llegó por el formulario de tikcontrol.app: no hay cuenta de TikControl detrás">
                      Desde la web (tikcontrol.app)
                    </span>
                  ) : (
                    <button className="text-left text-xs text-default-500 hover:underline" onClick={() => navigate(`/users/${it.uid}`)}>
                      Cuenta: {it.accountEmail || it.uid}
                    </button>
                  )}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                <div className="flex flex-col">
                  <span className={it.meetingAt ? 'font-medium' : 'text-default-400'}>{formatMeeting(it.meetingAt, !!it.meetingToken)}</span>
                  {formatMeetingLocal(it.meetingAt, it.meetingTz) && (
                    <span className="text-xs text-default-500" title="La hora de la reunión en la zona horaria de la agencia">
                      Su hora: {formatMeetingLocal(it.meetingAt, it.meetingTz)}
                    </span>
                  )}
                  {!it.meetingAt && it.meetingToken && it.status !== 'discarded' && (
                    <button
                      className="text-left text-xs text-primary hover:underline"
                      title="El mismo enlace que recibió la agencia por correo"
                      onClick={() => navigator.clipboard.writeText(bookingUrl(it)).then(() => setCopiedId(it.id), () => setError('No se pudo copiar el enlace'))}
                    >
                      {copiedId === it.id ? 'Enlace copiado' : 'Copiar enlace de reserva'}
                    </button>
                  )}
                  {missingInCalendar(it) && (
                    <span className="text-xs font-medium text-warning-600" title="Se reservó sin Google Calendar (sin conectar, caducado o caído): la reunión no está en el calendario del equipo ni tiene Google Meet">
                      No está en Google Calendar
                    </span>
                  )}
                  {missingInCalendar(it) && canWrite && (
                    <button
                      className="text-left text-xs text-primary hover:underline disabled:opacity-50"
                      title="Crea la reunión en el calendario conectado, con su Google Meet, y le manda a la agencia el correo con el enlace"
                      disabled={!!it.creating}
                      onClick={() => addToCalendar(it)}
                    >
                      {it.creating ? 'Creando…' : 'Crear en Google Calendar'}
                    </button>
                  )}
                  {it.calendarEventId && <span className="text-xs text-default-500">En Google Calendar</span>}
                  {it.calendarEventId && it.meetUrl && (
                    <a className="text-xs text-primary hover:underline" href={it.meetUrl} target="_blank" rel="noopener noreferrer">Google Meet</a>
                  )}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatTs(it.createdAt)}</TableCell>
              <TableCell>
                {canWrite ? (
                  <Select
                    size="sm"
                    aria-label="Estado"
                    className="w-36"
                    selectedKeys={[it.status || 'new']}
                    onSelectionChange={(keys) => setStatus(it, String(Array.from(keys)[0] || ''))}
                  >
                    {APPLICATION_STATUS.map((s) => <SelectItem key={s.key}>{s.label}</SelectItem>)}
                  </Select>
                ) : (
                  <Chip size="sm" variant="flat" color={STATUS_BY_KEY[it.status]?.color || 'default'}>{STATUS_BY_KEY[it.status]?.label || it.status}</Chip>
                )}
              </TableCell>
              <TableCell>
                {canWrite && <Button size="sm" variant="light" color="danger" onPress={() => remove(it)}>Borrar</Button>}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
